import { useEffect, useState } from 'react';
import { Loader2, Wand2, Shield, Info } from 'lucide-react';

interface ImprovementProgressModalProps {
  visible: boolean;
  startTime?: number;
}

/**
 * Overlay-modal som vises under generering av KI-forbedringsforslag.
 *
 * Tilsvarer ValidationProgressModal, men med faser tilpasset improve-endepunktet:
 * analyse av valideringsresultat, generering av forslag for læringsutbytter,
 * ESCO-forslag, kategoriforslag, og sammenstilling.
 *
 * Låser resten av UI-en (fixed inset-0 + backdrop-blur + z-50) slik at
 * studieplanen ikke kan endres mens KI-en jobber.
 */

const PHASE_MESSAGES = [
  { title: 'Lagrer endringer', detail: 'Sikrer at siste versjon er lagret før KI-en analyserer', minSec: 0 },
  { title: 'Analyserer valideringsresultatet', detail: 'Gjennomgår scores, kommentarer og flaggede læringsutbytter fra siste KI-validering', minSec: 3 },
  { title: 'Genererer forbedringsforslag', detail: 'KI foreslår konkrete forbedringer på tittel, beskrivelse, læringsutbytter og vurderingskriterier', minSec: 10 },
  { title: 'Matcher ESCO-ferdigheter', detail: 'Foreslår relevante ESCO-koblinger for foreslåtte læringsutbytter', minSec: 30 },
  { title: 'Foreslår kategorier', detail: 'Basert på studieplanens innhold og yrkesroller — kategorier for kompetanseheving.no', minSec: 45 },
  { title: 'Sammenstiller forslagene', detail: 'Pakker sammen alt til strukturerte forslag du kan velge å ta i bruk', minSec: 55 },
];

export default function ImprovementProgressModal({ visible, startTime }: ImprovementProgressModalProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const start = startTime || Date.now();
    setElapsed(Math.floor((Date.now() - start) / 1000));
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => clearInterval(interval);
  }, [visible, startTime]);

  if (!visible) return null;

  const activePhaseIdx = PHASE_MESSAGES.reduce((idx, phase, i) => {
    return elapsed >= phase.minSec ? i : idx;
  }, 0);
  const activePhase = PHASE_MESSAGES[activePhaseIdx];

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const elapsedText = minutes > 0
    ? `${minutes} min ${seconds} sek`
    : `${seconds} sek`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="improvement-progress-title"
    >
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg mx-4 w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-kf-primary-10 rounded-lg">
            <Wand2 className="text-kf-primary" size={24} />
          </div>
          <div>
            <h2 id="improvement-progress-title" className="text-xl font-bold text-kf-dark">
              KI-forbedringsforslag pågår
            </h2>
            <p className="text-sm text-gray-500">
              Studieplanen er låst mens KI-en analyserer og foreslår forbedringer
            </p>
          </div>
        </div>

        {/* Fase-info */}
        <div className="bg-kf-light border border-kf-primary-40 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Loader2 className="text-kf-primary animate-spin flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-kf-dark mb-1">
                {activePhase.title}
              </div>
              <div className="text-sm text-gray-700">
                {activePhase.detail}
              </div>
            </div>
          </div>
        </div>

        {/* Fremdriftsbar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Steg {activePhaseIdx + 1} av {PHASE_MESSAGES.length}</span>
            <span>{elapsedText}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-kf-primary transition-all duration-500"
              style={{
                width: `${Math.min(100, ((activePhaseIdx + 1) / PHASE_MESSAGES.length) * 100)}%`
              }}
            />
          </div>
        </div>

        {/* Info-boks */}
        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3 text-sm text-kf-blue-900 flex items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5 text-kf-blue-600" />
          <div>
            <div className="font-medium mb-0.5">Ikke lukk denne siden</div>
            <div className="text-kf-blue-800 text-xs">
              Forslag-generering tar typisk 30–90 sekunder. Når KI-en er ferdig
              kan du velge hvilke forslag du vil ta i bruk — ingen endringer skjer
              automatisk. Hvis prosessen henger lenger enn 3 minutter, kan du trygt
              laste siden på nytt.
            </div>
          </div>
        </div>

        {/* Personvern-note */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500">
          <Shield size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Studieplaner inneholder kun faglig innhold (læringsutbytter, moduler, ECTS)
            — ingen personopplysninger sendes til KI-tjenesten.
          </span>
        </div>
      </div>
    </div>
  );
}
